import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Award } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';

interface VerificationResult {
  valid: boolean;
  certificate?: {
    code: string;
    recipient_name: string;
    class_name: string;
    class_type: string;
    offline_hours: number;
    online_hours: number;
    issued_at: string;
    provider_name: string;
    is_revoked: boolean;
  };
  error?: string;
}

export default function CertificateVerifyPage() {
  const { code } = useParams();

  const [result, setResult] = useState<VerificationResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) {
      setResult({
        valid: false,
        error: '인증서 코드가 제공되지 않았습니다.',
      });
      setLoading(false);
      return;
    }
    verifyCertificate();
  }, [code]);

  const verifyCertificate = async () => {
    try {
      const response = await fetch(`/api/v1/verify/${code}`);

      if (!response.ok) {
        throw new Error('인증서 검증에 실패했습니다.');
      }

      const data = await response.json();
      setResult(data);
    } catch (error: any) {
      console.error('Failed to verify certificate:', error);
      setResult({
        valid: false,
        error: error.message || '인증서 검증 중 오류가 발생했습니다.',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">인증서 검증 중...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full">
          {/* StampFit Logo/Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <Award className="w-10 h-10 text-primary" />
              <h1 className="text-3xl font-bold text-gray-900">StampFit</h1>
            </div>
            <p className="text-gray-600">인증서 검증 시스템</p>
          </div>

          {/* Verification Result Card */}
          <div className="bg-white rounded-lg shadow-lg border-2 overflow-hidden">
            {result?.valid && result.certificate ? (
              <>
                {/* Valid Certificate */}
                <div
                  className={`p-6 text-center ${
                    result.certificate.is_revoked ? 'bg-red-50' : 'bg-green-50'
                  }`}
                >
                  {result.certificate.is_revoked ? (
                    <>
                      <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                      <h2 className="text-2xl font-bold text-red-900 mb-2">
                        취소된 인증서입니다
                      </h2>
                      <p className="text-red-700">
                        이 인증서는 발급 업체에 의해 취소되었습니다.
                      </p>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h2 className="text-2xl font-bold text-green-900 mb-2">
                        유효한 인증서입니다
                      </h2>
                      <p className="text-green-700">
                        이 인증서는 <span className="font-semibold">{result.certificate.provider_name}</span>에서 정식 발급된 인증서입니다.
                      </p>
                    </>
                  )}
                </div>

                {/* Certificate Details */}
                <div className="p-6 space-y-4">
                  <div className="border-b pb-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                      인증서 정보
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">수강생:</span>
                        <span className="font-semibold text-gray-900">
                          {result.certificate.recipient_name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">수업명:</span>
                        <span className="font-semibold text-gray-900">
                          {result.certificate.class_name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">발급 업체:</span>
                        <span className="font-semibold text-gray-900">
                          {result.certificate.provider_name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">발급일:</span>
                        <span className="font-semibold text-gray-900">
                          {formatDate(result.certificate.issued_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                      인증번호
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="font-mono text-center text-gray-900">
                        {result.certificate.code}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">인증서 종류:</span>
                    <span className="font-semibold text-gray-900">
                      {
                        {
                          CERTIFICATE: '자격증',
                          COMPLETION: '수료증',
                          WORKSHOP: '워크샵',
                          OTHER: '기타',
                        }[result.certificate.class_type] || '기타'
                      }
                    </span>
                  </div>
                  {(result.certificate.offline_hours > 0 || result.certificate.online_hours > 0) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">참여 시간:</span>
                      <span className="font-semibold text-gray-900">
                        {(() => {
                          const off = result.certificate.offline_hours || 0;
                          const on = result.certificate.online_hours || 0;
                          const total = off + on;
                          if (off > 0 && on > 0) {
                            return `${total}h (on-line ${on}h, off-line ${off}h)`;
                          } else if (on > 0) {
                            return `${total}h (on-line ${on}h)`;
                          } else {
                            return `${total}h (off-line ${off}h)`;
                          }
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Invalid Certificate */}
                <div className="p-6 text-center bg-red-50">
                  <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-red-900 mb-2">
                    유효하지 않은 인증서입니다
                  </h2>
                  <p className="text-red-700">
                    {result?.error || '인증서를 찾을 수 없습니다.'}
                  </p>
                </div>

                <div className="p-6 bg-gray-50">
                  <p className="text-sm text-gray-600 text-center">
                    인증서 코드: <span className="font-mono">{code}</span>
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer Info */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              이 페이지는 StampFit 인증서의 유효성을 검증하기 위한 공개 페이지입니다.
            </p>
            <p className="mt-2">
              인증서에 대한 문의사항은 발급 업체에 직접 문의하시기 바랍니다.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
